/**
 * TwoFactorInput Component
 * Component for entering 2FA verification code
 */

import { useState, useRef, KeyboardEvent, ClipboardEvent } from 'react';
import { Box, TextField, Button, Alert, Typography, CircularProgress } from '@mui/material';
import useAuthStore from '@/store/authStore';

const CODE_LENGTH = 6;

interface TwoFactorInputProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function TwoFactorInput({ onSuccess, onCancel }: TwoFactorInputProps) {
  const { verifyTwoFactor, status, error, clearError } = useAuthStore();
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isLoading = status === 'loading';

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every((digit) => digit !== '') && newCode.length === CODE_LENGTH) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    // Only accept digits
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, CODE_LENGTH).split('');
    const newCode = [...code];

    digits.forEach((digit, i) => {
      newCode[i] = digit;
    });

    setCode(newCode);

    // Focus the next empty input or the last one
    const nextIndex = Math.min(digits.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();

    // Auto-submit if complete
    if (newCode.every((digit) => digit !== '') && newCode.length === CODE_LENGTH) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleSubmit = async (codeValue: string) => {
    clearError();

    try {
      await verifyTwoFactor(codeValue);
      onSuccess?.();
    } catch (error) {
      // Clear code on error
      setCode(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      console.error('2FA verification error:', error);
    }
  };

  const handleManualSubmit = () => {
    const codeValue = code.join('');
    if (codeValue.length === CODE_LENGTH) {
      handleSubmit(codeValue);
    }
  };

  const handleCancel = () => {
    setCode(Array(CODE_LENGTH).fill(''));
    clearError();
    onCancel?.();
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom fontWeight={600}>
        Autenticación de Dos Factores
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ingresa el código de 6 dígitos de tu aplicación de autenticación
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          mb: 3,
        }}
      >
        {code.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '24px',
                fontWeight: 600,
              },
            }}
            sx={{
              width: 48,
              '& input': {
                padding: '12px 0',
              },
            }}
            disabled={isLoading}
            autoFocus={index === 0}
          />
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading || code.join('').length !== CODE_LENGTH}
        onClick={handleManualSubmit}
        sx={{ mb: 1 }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Verificando...
          </>
        ) : (
          'Verificar'
        )}
      </Button>

      <Button fullWidth variant="text" onClick={handleCancel} disabled={isLoading}>
        Cancelar
      </Button>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          ¿No tienes acceso a tu código?{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => alert('Funcionalidad de códigos de respaldo próximamente')}
          >
            Usar código de respaldo
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
}
